import { SponsorProps, SponsorsGroupProps } from '@stadionhq-v3/base-ui';
import { Locale } from '@core/constants/locales.constants';
import { Image, ImageLink, SponsorGroup } from '@graphql/index';
import { CONTENT_TYPES } from '@core/constants/contentTypes.constants';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import {
  CleanContentful,
  CleanedContentfulImageProps,
} from '@stadionhq-v3/clean-contentful';
import { imageLinkTransform } from '../atoms/imageLink.transform';

const MAX_SPONSOR_IMAGE_SIZE = 150;

const capImageDimensions = (
  image: CleanedContentfulImageProps
): CleanedContentfulImageProps => {
  const { width, height } = image;
  if (!width || !height) return image;

  const scale = Math.min(
    MAX_SPONSOR_IMAGE_SIZE / width,
    MAX_SPONSOR_IMAGE_SIZE / height,
    1
  );

  return {
    ...image,
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

const sponsorGroupTransform = (
  sponsorGroup: CleanContentful<SponsorGroup>,
  hideAgeSensitiveContent: boolean,
  locale: Locale
): SponsorsGroupProps => {
  const shouldHideSponsor = (
    sponsor: CleanContentful<Image> | CleanContentful<ImageLink>
  ) => {
    switch (sponsor.__typename) {
      case CONTENT_TYPES.IMAGE_TAG:
        return (
          (sponsor as CleanedContentfulImageProps).isAgeSensitiveContent &&
          hideAgeSensitiveContent
        );
      case CONTENT_TYPES.IMAGE_LINK_TAG:
        return (
          (sponsor as CleanContentful<ImageLink>).image
            ?.isAgeSensitiveContent && hideAgeSensitiveContent
        );
      default:
        return false;
    }
  };

  const transformSponsor = (
    sponsor: CleanContentful<Image> | CleanContentful<ImageLink>
  ) => {
    if (sponsor.__typename === CONTENT_TYPES.IMAGE_TAG) {
      const sponsorImage = capImageDimensions(
        sponsor as CleanedContentfulImageProps
      );

      return {
        logo: <ContentfulImage {...sponsorImage} />,
        title: sponsorImage.alt,
      };
    }

    const imageLink = sponsor as CleanContentful<ImageLink>;
    const cappedImageLink = imageLink.image
      ? {
          ...imageLink,
          image: capImageDimensions(
            imageLink.image as CleanedContentfulImageProps
          ),
        }
      : imageLink;

    return imageLinkTransform(
      cappedImageLink as CleanContentful<ImageLink>,
      locale
    );
  };

  return {
    ...sponsorGroup,
    sponsors: sponsorGroup?.sponsors
      ?.map((sponsor: CleanContentful<Image> | CleanContentful<ImageLink>) => {
        if (!sponsor) {
          return null;
        }

        if (shouldHideSponsor(sponsor)) {
          return null;
        }

        return transformSponsor(sponsor);
      })
      .filter(Boolean) as SponsorProps[],
  };
};

export default sponsorGroupTransform;
